import React, { useState } from "react";
import { connect } from "react-redux";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";

const Forgot = ({ forgotPassword }) => {
  const [sendEmail, setSendEmail] = useState("");
  const [responseStatus, setResponseStatus] = useState(true);
  const [isLoading, setLoading] = useState(false);

  const forgotPasswordSubmit = async (values) => {
    try {
      setLoading(true);

      const { message, success } = await forgotPassword({
        email: values.email,
      });

      setResponseStatus(success);
      setSendEmail(message);
    } catch (error) {
      setResponseStatus(false);
      setSendEmail("Something went wrong. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-white to-slate-200 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden">
        {/* Top Banner */}

        <div className="p-8 sm:p-10">
          {/* Logo */}
          <div className="flex flex-col items-center mb-8">
            <div className="p-4">
              <img
                src="/toobler-mark.jpg"
                alt="Toobler"
                className="h-16 w-auto"
              />
            </div>

            <h1 className="mt-6 text-2xl lg:text-3xl font-bold text-slate-900">
              Forgot Password
            </h1>

            <p className="mt-2 text-sm text-slate-500 text-center max-w-xs">
              Enter your registered email address and we'll send you a link to
              reset your password.
            </p>
          </div>

          <Formik
            initialValues={{
              email: "",
            }}
            validationSchema={Yup.object().shape({
              email: Yup.string()
                .email("Please enter a valid email address")
                .required("Email is required"),
            })}
            onSubmit={forgotPasswordSubmit}
          >
            {({ errors, touched }) => (
              <Form className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Email Address
                  </label>

                  <Field
                    name="email"
                    type="email"
                    placeholder="Enter your email"
                    className={`w-full rounded-xl border px-4 py-3 text-slate-700 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 transition-all duration-200 ${
                      errors.email && touched.email
                        ? "border-red-500 focus:ring-red-200"
                        : "border-slate-300 focus:border-black focus:ring-slate-200"
                    }`}
                  />

                  {errors.email && touched.email && (
                    <p className="mt-2 text-xs font-medium text-red-500">
                      {errors.email}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full rounded-xl bg-black py-3.5 text-white font-semibold transition-all duration-300 hover:bg-slate-800 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      <svg
                        className="h-5 w-5 animate-spin"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8H4z"
                        />
                      </svg>
                      Sending...
                    </div>
                  ) : (
                    "Send Reset Link"
                  )}
                </button>
              </Form>
            )}
          </Formik>

          {/* Response Message */}
          {sendEmail?.length > 0 && (
            <div
              className={`mt-6 rounded-xl border px-4 py-3 text-sm font-medium ${
                responseStatus
                  ? "border-green-200 bg-green-50 text-green-700"
                  : "border-red-200 bg-red-50 text-red-700"
              }`}
            >
              {sendEmail}
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 border-t border-slate-200 pt-6 text-center">
            <a
              href="/login"
              className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 transition-colors hover:text-black"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="h-5 w-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
                />
              </svg>

              <span>Back to Login</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapState = (state) => ({
  userInfo: state.auth.userInfo,
});

const mapDispatch = (dispatch) => ({
  forgotPassword: dispatch.auth.forgotPassword,
});

export default connect(mapState, mapDispatch)(Forgot);
